import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import AiLock from "@/components/AiLock";
import LicenseUpsell from "@/components/LicenseUpsell";
import AiExpiryBanner from "@/components/AiExpiryBanner";
import { useAuth } from "@/context/AuthContext";
import { api, API } from "@/lib/api";
import { ProjectInquiryButton } from "@/components/ProjectInquiry";
import { FileText, TrafficCone, Wand2, Loader2, CheckCircle2 } from "lucide-react";

export default function Tools() {
  const [tab, setTab] = useState("briefing");
  return (
    <div className="min-h-screen bg-background grain">
      <Navbar />
      <main className="max-w-4xl mx-auto px-5 sm:px-8 py-8">
        <h1 className="font-heading font-extrabold text-3xl sm:text-4xl mb-2">Praxis-Werkzeuge</h1>
        <p className="text-foreground/70 mb-7">Wenden Sie Ihr Wissen direkt auf echte Projekte an.</p>

        <AiExpiryBanner />

        <div className="flex gap-1 p-1 rounded-full bg-white/5 mb-8 w-full sm:w-fit">
          {[
            { k: "briefing", label: "Lichtbriefing-Generator", icon: FileText },
            { k: "ampel", label: "Normen-Ampel", icon: TrafficCone },
          ].map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.k}
                onClick={() => setTab(t.k)}
                data-testid={`tool-tab-${t.k}`}
                className={`flex items-center gap-2 px-4 sm:px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
                  tab === t.k ? "bg-gold text-[#0a0d12]" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <Icon className="h-4 w-4" /> <span className="hidden sm:inline">{t.label}</span>
              </button>
            );
          })}
        </div>

        {tab === "briefing" ? <BriefingTool /> : <AmpelTool />}
      </main>
    </div>
  );
}

function Input({ label, value, onChange, placeholder, testid }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-muted-foreground">{label}</span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="mt-1.5 w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 outline-none focus:border-gold/50 focus:ring-2 focus:ring-gold/20 transition-all text-foreground placeholder:text-muted-foreground/50"
      />
    </label>
  );
}

function BriefingTool() {
  const { user } = useAuth();
  const [form, setForm] = useState({
    roomType: "", usage: "", atmosphere: "", materials: "", ceilingHeight: "", daylight: "", budget: "", risks: "",
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState("");
  const [usage, setUsage] = useState(null);
  const [expired, setExpired] = useState(false);

  const aiActive = user && (user.role === "admin" || user.ai_active) && !expired;

  const loadUsage = () => api.get("/me/usage").then((r) => setUsage(r.data.briefing)).catch((e) => console.error("Usage konnte nicht geladen werden:", e));
  useEffect(() => { loadUsage(); }, []);

  const set = (k) => (v) => setForm({ ...form, [k]: v });

  const generate = async () => {
    if (!form.roomType || !form.usage) {
      toast.error("Bitte mindestens Raumtyp und Nutzung angeben.");
      return;
    }
    setLoading(true);
    setResult("");
    try {
      const token = localStorage.getItem("lux_token");
      const res = await fetch(`${API}/tools/briefing`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      if (!res.ok || !res.body) {
        let detail = "Briefing konnte nicht erstellt werden. Bitte erneut versuchen.";
        try {
          const data = await res.json();
          if (data?.detail) detail = data.detail;
        } catch (_) {}
        toast.error(detail);
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const payload = JSON.parse(line.slice(5).trim());
          if (payload.delta) setResult((r) => r + payload.delta);
        }
      }
    } catch (e) {
      console.error("Briefing-Stream fehlgeschlagen:", e);
      toast.error("Briefing konnte nicht erstellt werden. Bitte erneut versuchen.");
    } finally {
      setLoading(false);
      loadUsage();
    }
  };

  if (user?.role === "trial") return <LicenseUpsell feature="Der Lichtbriefing-Generator" />;
  if (!aiActive) return <AiLock feature="den Lichtbriefing-Generator" onUnlocked={() => setExpired(false)} />;

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
      <div className="rounded-2xl glass p-6 sm:p-7">
        <div className="grid sm:grid-cols-2 gap-4">
          <Input label="Raumtyp *" value={form.roomType} onChange={set("roomType")} placeholder="z.B. Boutique, Büro, Restaurant" testid="briefing-roomtype" />
          <Input label="Nutzung *" value={form.usage} onChange={set("usage")} placeholder="z.B. Verkauf, konzentriertes Arbeiten" testid="briefing-usage" />
          <Input label="Gewünschte Atmosphäre" value={form.atmosphere} onChange={set("atmosphere")} placeholder="z.B. hochwertig, warm, einladend" testid="briefing-atmosphere" />
          <Input label="Materialien / Farben" value={form.materials} onChange={set("materials")} placeholder="z.B. dunkles Holz, Messing, matt" testid="briefing-materials" />
          <Input label="Raumhöhe" value={form.ceilingHeight} onChange={set("ceilingHeight")} placeholder="z.B. 3,2 m" testid="briefing-height" />
          <Input label="Tageslicht" value={form.daylight} onChange={set("daylight")} placeholder="z.B. grosse Fenster Südseite" testid="briefing-daylight" />
          <Input label="Budget" value={form.budget} onChange={set("budget")} placeholder="z.B. mittel, gehoben" testid="briefing-budget" />
          <Input label="Bekannte Risiken" value={form.risks} onChange={set("risks")} placeholder="z.B. Blendung, glänzende Flächen" testid="briefing-risks" />
        </div>
        <button
          onClick={generate}
          disabled={loading}
          data-testid="briefing-generate"
          className="mt-6 inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-gold text-[#0a0d12] font-semibold hover:shadow-[0_8px_30px_-8px_rgba(212,175,55,0.7)] transition-all duration-300 disabled:opacity-60"
        >
          {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Erstelle Briefing…</> : <><Wand2 className="h-4 w-4" /> Lichtbriefing generieren</>}
        </button>
        {usage && usage.remaining !== null && (
          <span data-testid="briefing-usage" className="ml-4 text-xs text-muted-foreground">
            Heute noch <span className="text-gold/90">{usage.remaining}/{usage.limit}</span> Briefings
          </span>
        )}
      </div>

      <AnimatePresence>
        {result && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-6 rounded-2xl glass-strong p-6 sm:p-8"
            data-testid="briefing-result"
          >
            <div className="flex items-center gap-2 mb-4 text-gold">
              <FileText className="h-5 w-5" />
              <h3 className="font-heading font-bold text-lg">Ihr Lichtbriefing</h3>
            </div>
            <div className="prose-coach text-sm text-foreground/85 leading-relaxed whitespace-pre-wrap">{result}</div>
            {!loading && (
              <div className="mt-6 pt-6 border-t hairline flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
                <p className="text-sm text-foreground/70 max-w-md">
                  Dieses Projekt von <span className="text-foreground/90 font-medium">Lux Solutions GmbH</span> professionell
                  umsetzen lassen? Wir übernehmen Planung, Berechnung und Ausschreibung.
                </p>
                <ProjectInquiryButton
                  testid="project-inquiry-briefing-button"
                  source="briefing"
                  label="Dieses Projekt anfragen"
                  className="flex-shrink-0"
                  body={`Guten Tag\n\nÜber die LUX Architect Lighting Academy habe ich folgendes Lichtbriefing erstellt und möchte eine Lichtplanung / Projektanfrage stellen:\n\n--- LICHTBRIEFING ---\n${result}\n---------------------\n\nBitte kontaktieren Sie mich für die weitere Planung.\n\nFreundliche Grüsse`}
                />
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function Toggle({ label, value, onChange, testid }) {
  return (
    <button
      onClick={() => onChange(!value)}
      data-testid={testid}
      className={`flex items-center justify-between w-full px-4 py-3 rounded-xl border transition-all duration-300 ${
        value ? "bg-gold/15 border-gold/40 text-gold" : "bg-white/5 border-white/10 text-foreground/80 hover:bg-white/[0.08]"
      }`}
    >
      <span className="text-sm text-left">{label}</span>
      <span className={`h-5 w-9 rounded-full relative transition-colors ${value ? "bg-gold" : "bg-white/15"}`}>
        <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-[#0a0d12] transition-all ${value ? "left-4" : "left-0.5"}`} />
      </span>
    </button>
  );
}

const AMPEL = {
  green: { color: "#22c55e", glow: "rgba(34,197,94,0.4)" },
  yellow: { color: "#eab308", glow: "rgba(234,179,8,0.4)" },
  red: { color: "#ef4444", glow: "rgba(239,68,68,0.4)" },
};

function AmpelTool() {
  const [form, setForm] = useState({
    workplace: false, publicAccess: false, visualTask: "keine", glareRisk: false, darkMaterials: false, proofNeeded: false,
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm({ ...form, [k]: v });

  const evaluate = async () => {
    setLoading(true);
    try {
      const r = await api.post("/tools/normen-ampel", form);
      setResult(r.data);
    } catch (e) {
      toast.error("Bewertung fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
      <div className="rounded-2xl glass p-6 sm:p-7">
        <div className="space-y-3">
          <Toggle label="Es gibt einen Arbeitsplatz (Sehaufgabe nach Norm)" value={form.workplace} onChange={(v) => set("workplace", v)} testid="ampel-workplace" />
          <Toggle label="Öffentlich zugänglicher Bereich" value={form.publicAccess} onChange={(v) => set("publicAccess", v)} testid="ampel-public" />
          <div>
            <span className="text-xs uppercase tracking-widest text-muted-foreground">Sehaufgabe</span>
            <div className="mt-1.5 grid grid-cols-3 gap-2">
              {[["keine", "Keine"], ["einfach", "Einfach"], ["anspruchsvoll", "Anspruchsvoll"]].map(([k, l]) => (
                <button
                  key={k}
                  onClick={() => set("visualTask", k)}
                  data-testid={`ampel-task-${k}`}
                  className={`py-2.5 rounded-xl border text-sm transition-all ${form.visualTask === k ? "bg-gold/15 border-gold/40 text-gold" : "bg-white/5 border-white/10 text-foreground/80 hover:bg-white/[0.08]"}`}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
          <Toggle label="Blendungsrisiko (Bildschirm, glänzende Flächen)" value={form.glareRisk} onChange={(v) => set("glareRisk", v)} testid="ampel-glare" />
          <Toggle label="Dunkle Materialien / niedrige Reflexionsgrade" value={form.darkMaterials} onChange={(v) => set("darkMaterials", v)} testid="ampel-dark" />
          <Toggle label="Nachweispflicht (Abnahme, Behörde, Sicherheit)" value={form.proofNeeded} onChange={(v) => set("proofNeeded", v)} testid="ampel-proof" />
        </div>
        <button
          onClick={evaluate}
          disabled={loading}
          data-testid="ampel-evaluate"
          className="mt-6 inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-gold text-[#0a0d12] font-semibold hover:shadow-[0_8px_30px_-8px_rgba(212,175,55,0.7)] transition-all duration-300 disabled:opacity-60"
        >
          {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Bewerte…</> : <><TrafficCone className="h-4 w-4" /> Einschätzung anzeigen</>}
        </button>
      </div>

      <AnimatePresence>
        {result && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-6 rounded-2xl glass-strong p-6 sm:p-8"
            data-testid="ampel-result"
          >
            <div className="flex items-center gap-4 mb-5">
              <div className="flex flex-col gap-2 p-3 rounded-xl bg-black/30 border border-white/10">
                {["red", "yellow", "green"].map((c) => (
                  <span
                    key={c}
                    className="h-5 w-5 rounded-full transition-all"
                    style={{
                      background: result.level === c ? AMPEL[c].color : "rgba(255,255,255,0.08)",
                      boxShadow: result.level === c ? `0 0 16px ${AMPEL[c].glow}` : "none",
                    }}
                  />
                ))}
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Einschätzung</div>
                <h3 className="font-heading font-bold text-2xl" style={{ color: AMPEL[result.level].color }}>{result.title}</h3>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <h4 className="text-sm font-semibold text-foreground/90 mb-2">Begründung</h4>
                <ul className="space-y-2">
                  {result.reasons.map((r, i) => (
                    <li key={i} className="text-sm text-foreground/75 flex gap-2"><span className="h-1.5 w-1.5 rounded-full bg-gold mt-1.5 flex-shrink-0" />{r}</li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground/90 mb-2">Empfehlungen</h4>
                <ul className="space-y-2">
                  {result.recommendations.map((r, i) => (
                    <li key={i} className="text-sm text-foreground/75 flex gap-2"><CheckCircle2 className="h-4 w-4 text-gold mt-0.5 flex-shrink-0" />{r}</li>
                  ))}
                </ul>
              </div>
            </div>

            {result.level === "red" && (
              <div className="mt-6 pt-6 border-t hairline flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
                <p className="text-sm text-foreground/70 max-w-md">
                  Fachplanung erforderlich? <span className="text-foreground/90 font-medium">Lux Solutions GmbH</span> übernimmt
                  den normgerechten Nachweis und die Lichtplanung für Ihr Projekt.
                </p>
                <ProjectInquiryButton
                  testid="project-inquiry-ampel-button"
                  source="ampel"
                  label="Fachplanung anfragen"
                  className="flex-shrink-0"
                  body={"Guten Tag\n\nDie Normen-Ampel der LUX Architect Lighting Academy hat für mein Projekt \"Fachplanung erforderlich\" ergeben. Ich möchte eine Anfrage für eine normgerechte Lichtplanung stellen.\n\nBitte kontaktieren Sie mich für die weitere Planung.\n\nFreundliche Grüsse"}
                />
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
